/**
 * Google Analytics 4 (gtag.js) con Consent Mode v2.
 *
 * Lo stato `consent default` (tutto `denied`) è impostato in index.html, prima
 * di qualunque caricamento di GA. Questo modulo:
 *   - carica gtag.js a runtime SOLO in produzione e SOLO se VITE_GA4_ID è
 *     valorizzato (no-op altrimenti — stesso pattern di src/lib/sentry.ts);
 *   - aggiorna il consenso (`gtag('consent','update',...)`) quando l'utente
 *     sceglie nel banner;
 *   - invia i page_view a mano a ogni cambio rotta (vedi usePageViews).
 *
 * Niente PII negli eventi (no email/telefono): regola di progetto.
 */

import { env } from '@/config/env'

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

const GA_ID = env.analytics.gaId

/** True se possiamo davvero tracciare (prod + ID presente). */
function analyticsEnabled(): boolean {
  return env.isProd && Boolean(GA_ID)
}

/** `gtag` è definito in index.html; qui un accesso difensivo. */
function gtag(...args: unknown[]): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  window.gtag(...args)
}

let initialized = false

/**
 * Carica gtag.js e configura GA. Idempotente: chiamabile più volte (es. al
 * primo "Accetta" e poi ai reload). No-op fuori produzione o senza ID.
 */
export function initAnalytics(): void {
  if (initialized || !analyticsEnabled()) return
  initialized = true

  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(s)

  // `gtag('js', ...)` vuole un Date reale; resta confinato qui.
  gtag('js', new Date())
  // send_page_view: false → i page_view li inviamo noi a ogni cambio rotta,
  // evitando il doppio conteggio col page_view automatico di GA.
  gtag('config', GA_ID, { send_page_view: false })
}

/**
 * Cancella fisicamente i cookie scritti da GA4 (`_ga`, `_ga_<ID>`, `_gid`...).
 * Il Consent Mode "denied" già blocca l'uso dei cookie, ma alla revoca li
 * rimuoviamo anche fisicamente per massima pulizia/conformità.
 *
 * GA scrive i cookie sul dominio registrabile (es. `.areaclienti.techus.it`),
 * non sull'host esatto: per cancellarli davvero proviamo a scadere ogni
 * variante di dominio (host corrente e tutti i suffissi punto-padre).
 */
function clearGaCookies(): void {
  if (typeof document === 'undefined') return
  const hostname = window.location.hostname
  // Domini candidati: "host", ".host", ".sub.dominio", ... fino a ".dominio.tld".
  const parts = hostname.split('.')
  const domains = new Set<string>([hostname, `.${hostname}`])
  for (let i = 0; i < parts.length - 1; i++) {
    domains.add('.' + parts.slice(i).join('.'))
  }

  const gaCookies = document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((name) => name === '_ga' || name.startsWith('_ga') || name === '_gid')

  for (const name of gaCookies) {
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`
    }
    // anche senza domain esplicito (cookie scritto sull'host puro)
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
  }
}

/**
 * Aggiorna il Consent Mode in base alla scelta dell'utente sulla categoria
 * "analitici". Va chiamata anche all'avvio se c'è un consenso già salvato.
 * Alla revoca (denied) rimuove anche fisicamente i cookie GA già presenti.
 */
export function updateConsent(analyticsGranted: boolean): void {
  if (!analyticsEnabled()) return
  gtag('consent', 'update', {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
  })
  if (!analyticsGranted) clearGaCookies()
}

/** Invia un page_view per la rotta corrente. No-op se GA non è attivo. */
export function trackPageView(path: string): void {
  if (!initialized || !analyticsEnabled()) return
  gtag('event', 'page_view', { page_path: path })
}

/**
 * All'avvio dell'app, riapplica un consenso già salvato in una sessione
 * precedente: aggiorna il Consent Mode e, se gli analitici erano stati
 * accettati, avvia GA. Va chiamata una volta in fase di bootstrap (main.tsx).
 * No-op fuori produzione / senza ID.
 */
export function syncConsentOnStartup(decided: boolean, analyticsGranted: boolean): void {
  if (!analyticsEnabled() || !decided) return
  updateConsent(analyticsGranted)
  if (analyticsGranted) initAnalytics()
}
